/* =============================================================
   ConsultationCRMPage — /admin/crm
   CRM for consultation requests:
   - Table with search + status/country filters
   - Slide-in detail panel: status, probability, notes, assignedTo, callbackAt
   - Stats bar: total, by status
   ============================================================= */
import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import {
  Search, Filter, Phone, Mail, Globe, Calendar,
  Clock, User, ChevronRight, X, Check, ArrowLeft,
  TrendingUp, AlertCircle, RefreshCw, CheckCircle2,
  Trash2, PhoneCall,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { STAFF_MEMBERS } from "@shared/staff";

/* ── Types ───────────────────────────────────────────────────── */
type Status = "new" | "phone_consulted" | "callback" | "office_consulted";
type FilterStatus = "all" | Status;

/* ── Constants ───────────────────────────────────────────────── */
const STATUS_CONFIG: Record<Status, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  new:              { label: "Шинэ",                  color: "#60a5fa", bg: "rgba(96,165,250,0.12)",  icon: <AlertCircle className="w-3.5 h-3.5" /> },
  phone_consulted:  { label: "Утсаар зөвлөгөө өгсөн", color: "#34d399", bg: "rgba(52,211,153,0.12)",  icon: <PhoneCall className="w-3.5 h-3.5" /> },
  callback:         { label: "Дахин залгах",          color: "#fbbf24", bg: "rgba(251,191,36,0.12)",  icon: <RefreshCw className="w-3.5 h-3.5" /> },
  office_consulted: { label: "Оффист зөвлөгөө өгсөн", color: "#c084fc", bg: "rgba(192,132,252,0.12)", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
};

const COUNTRY_LABELS: Record<string, string> = {
  usa: "🇺🇸 АНУ", australia: "🇦🇺 Австрали", canada: "🇨🇦 Канад",
  ireland: "🇮🇪 Ирланд", uk: "🇬🇧 Их Британи", schengen: "🇪🇺 Шенген",
  czech: "🇨🇿 Чех", newzealand: "🇳🇿 Шинэ Зеланд", other: "🌍 Бусад",
};

const CALL_TIME_LABELS: Record<string, string> = {
  now: "⚡ Яг одоо", morning: "🌅 Өглөө", afternoon: "☀️ Үдээс хойш",
};

function formatDate(d: Date | string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("mn-MN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ProbabilityBar({ value }: { value: number | null | undefined }) {
  const pct = value ?? 0;
  const color = pct >= 70 ? "#34d399" : pct >= 40 ? "#fbbf24" : "#f87171";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color }}>{pct}%</span>
    </div>
  );
}

/* ── Detail Panel ────────────────────────────────────────────── */
function DetailPanel({
  id,
  onClose,
  onUpdated,
}: {
  id: number;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { data: item, isLoading } = trpc.consultation.getById.useQuery({ id });
  const updateMut = trpc.consultation.update.useMutation({ onSuccess: () => { onUpdated(); } });
  const reassignMut = trpc.consultation.reassign.useMutation({ onSuccess: () => { onUpdated(); } });
  const deleteMut = trpc.consultation.delete.useMutation({ onSuccess: () => { onClose(); onUpdated(); } });

  const [editNotes, setEditNotes] = useState<string | null>(null);
  const [editProb, setEditProb] = useState<string | null>(null);
  const [editCallback, setEditCallback] = useState<string | null>(null);

  if (isLoading || !item) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 rounded-full border-2 border-violet-400/30 border-t-violet-400 animate-spin" />
      </div>
    );
  }

  const sc = STATUS_CONFIG[item.status as Status];

  const saveField = (field: string, value: unknown) => {
    updateMut.mutate({ id, [field]: value });
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div>
          <h2 className="text-white font-bold text-lg">{item.name}</h2>
          <p className="text-white/45 text-sm mt-0.5">#{item.id} · {formatDate(item.createdAt)}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/50">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-col gap-5 p-5">
        {/* Contact info */}
        <div
          className="rounded-xl p-4 flex flex-col gap-3"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <p className="text-white/40 text-xs font-semibold uppercase tracking-wide">Холбоо барих</p>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Phone className="w-3.5 h-3.5 text-white/40" />
            <a href={`tel:${item.phone}`} className="hover:text-white transition-colors">{item.phone}</a>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Mail className="w-3.5 h-3.5 text-white/40" />
            <a href={`mailto:${item.email}`} className="hover:text-white transition-colors">{item.email}</a>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Globe className="w-3.5 h-3.5 text-white/40" />
            <span>{COUNTRY_LABELS[item.country] ?? item.country} — {item.visaCategory}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Calendar className="w-3.5 h-3.5 text-white/40" />
            <span>{item.travelTimeline}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Clock className="w-3.5 h-3.5 text-white/40" />
            <span>{CALL_TIME_LABELS[item.callTime] ?? item.callTime}</span>
          </div>
        </div>

        {/* Status */}
        <div>
          <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">Статус</p>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(STATUS_CONFIG) as Status[]).map(s => {
              const cfg = STATUS_CONFIG[s];
              const active = item.status === s;
              return (
                <button
                  key={s}
                  onClick={() => saveField("status", s)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{
                    background: active ? cfg.bg : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? cfg.color + "50" : "rgba(255,255,255,0.08)"}`,
                    color: active ? cfg.color : "rgba(255,255,255,0.50)",
                  }}
                >
                  {cfg.icon}
                  {cfg.label}
                  {active && <Check className="w-3 h-3 ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Probability */}
        <div>
          <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">
            <TrendingUp className="w-3 h-3 inline mr-1" />Гэрээ хийх магадлал
          </p>
          <ProbabilityBar value={item.probability} />
          <div className="flex items-center gap-2 mt-2">
            <input
              type="number"
              min={0} max={100}
              value={editProb ?? (item.probability ?? 0)}
              onChange={e => setEditProb(e.target.value)}
              className="w-20 px-3 py-2 rounded-lg text-white text-sm outline-none"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
            />
            <span className="text-white/40 text-sm">%</span>
            <button
              onClick={() => { saveField("probability", Number(editProb ?? item.probability ?? 0)); setEditProb(null); }}
              className="px-3 py-2 rounded-lg text-xs font-bold transition-all"
              style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa", border: "1px solid rgba(124,58,237,0.30)" }}
            >
              Хадгалах
            </button>
          </div>
        </div>

        {/* Assigned to */}
        <div>
          <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">
            <User className="w-3 h-3 inline mr-1" />Хариуцах ажилтан
          </p>
          <select
            value={item.assignedTo ?? ""}
            onChange={e => reassignMut.mutate({ id, assignedTo: e.target.value ? STAFF_MEMBERS.find(name => name === e.target.value) ?? null : null })}
            className="w-full px-3 py-2 rounded-lg text-white text-sm outline-none appearance-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
          >
            <option value="" style={{ background: "#130D3A" }}>Суллах — шинэ хүсэлт болгох</option>
            {STAFF_MEMBERS.map(name => <option key={name} value={name} style={{ background: "#130D3A" }}>{name}</option>)}
          </select>
        </div>

        {/* Callback at */}
        <div>
          <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">
            <RefreshCw className="w-3 h-3 inline mr-1" />Дахин залгах цаг
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Жишээ: Маргааш 10:00"
              value={editCallback ?? (item.callbackAt ?? "")}
              onChange={e => setEditCallback(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg text-white text-sm outline-none placeholder-white/25"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
            />
            <button
              onClick={() => { saveField("callbackAt", editCallback ?? item.callbackAt ?? ""); setEditCallback(null); }}
              className="px-3 py-2 rounded-lg text-xs font-bold transition-all"
              style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa", border: "1px solid rgba(124,58,237,0.30)" }}
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Last contacted */}
        {item.lastContactedAt && (
          <p className="text-white/30 text-xs text-center">
            Сүүлд холбогдсон: {formatDate(item.lastContactedAt)}
          </p>
        )}

        {/* Notes */}
        <div>
          <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">Тэмдэглэл</p>
          <textarea
            rows={4}
            placeholder="Ажилтны тэмдэглэл..."
            value={editNotes ?? (item.notes ?? "")}
            onChange={e => setEditNotes(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25 resize-none"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
          />
          <button
            onClick={() => { saveField("notes", editNotes ?? item.notes ?? ""); setEditNotes(null); }}
            className="mt-2 px-4 py-2 rounded-lg text-xs font-bold transition-all w-full"
            style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa", border: "1px solid rgba(124,58,237,0.30)" }}
          >
            Тэмдэглэл хадгалах
          </button>
        </div>

        {/* Delete */}
        <button
          onClick={() => { if (confirm("Устгах уу?")) deleteMut.mutate({ id }); }}
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-medium transition-all mt-2"
          style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.20)", color: "#f87171" }}
        >
          <Trash2 className="w-4 h-4" />
          Устгах
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────── */
export default function ConsultationCRMPage() {
  const { user, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();

  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [filterCountry, setFilterCountry] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: rows = [], refetch } = trpc.consultation.list.useQuery(
    { status: filterStatus, country: filterCountry || undefined, search: search || undefined },
    { enabled: isAuthenticated && user?.role === "admin" }
  );

  // Stats
  const stats = useMemo(() => {
    const all = rows.length;
    const byStatus = { new: 0, phone_consulted: 0, office_consulted: 0, callback: 0 };
    rows.forEach(r => { if (r.status in byStatus) byStatus[r.status as Status]++; });
    return { all, ...byStatus };
  }, [rows]);

  if (!isAuthenticated || user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0D0A2E" }}>
        <p className="text-white/50">Зөвхөн admin хандах боломжтой.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 100%)" }}>
      {/* ── Left: Table ── */}
      <div
        className="flex flex-col flex-1 min-w-0"
        style={{ borderRight: selectedId ? "1px solid rgba(255,255,255,0.08)" : "none" }}
      >
        {/* Top bar */}
        <div
          className="flex items-center gap-3 px-5 py-4 flex-wrap"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-1.5 text-white/50 hover:text-white/80 transition-colors text-sm mr-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Admin
          </button>
          <h1 className="text-white font-bold text-lg mr-auto">Зөвлөгөө авах хүсэлт — CRM</h1>
          <span className="text-white/40 text-sm">{rows.length} хүсэлт</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          {([
            ["all", "Бүгд", stats.all, "#a78bfa"],
            ["new", "Шинэ", stats.new, "#60a5fa"],
            ["phone_consulted", "Утсаар", stats.phone_consulted, "#34d399"],
            ["callback", "Дахин залгах", stats.callback, "#fbbf24"],
            ["office_consulted", "Оффист", stats.office_consulted, "#c084fc"],
          ] as [FilterStatus, string, number, string][]).map(([s, label, count, color]) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className="flex flex-col items-center py-3 px-2 transition-all"
              style={{
                background: filterStatus === s ? "rgba(124,58,237,0.10)" : "transparent",
                borderBottom: filterStatus === s ? `2px solid ${color}` : "2px solid transparent",
              }}
            >
              <span className="text-xl font-black" style={{ color }}>{count}</span>
              <span className="text-white/40 text-[10px] font-medium mt-0.5">{label}</span>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 px-5 py-3 flex-wrap" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="text"
              placeholder="Нэр, утас, имэйл..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-white text-sm outline-none placeholder-white/25"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
            />
          </div>
          {/* Country filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <select
              value={filterCountry}
              onChange={e => setFilterCountry(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-lg text-white text-sm outline-none appearance-none cursor-pointer"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", color: filterCountry ? "white" : "rgba(255,255,255,0.40)" }}
            >
              <option value="">Бүх улс</option>
              {Object.entries(COUNTRY_LABELS).map(([v, l]) => (
                <option key={v} value={v} style={{ background: "#130D3A" }}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <p className="text-white/30 text-sm">Хүсэлт олдсонгүй</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  {["Нэр / Утас", "Улс / Виз", "Явах хугацаа", "Статус", "Магадлал", "Ажилтан", "Огноо", ""].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-white/35 text-xs font-semibold uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const sc = STATUS_CONFIG[row.status as Status] ?? STATUS_CONFIG.new;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      className="cursor-pointer transition-colors hover:bg-white/[0.03]"
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        background: selectedId === row.id ? "rgba(124,58,237,0.08)" : "transparent",
                      }}
                    >
                      <td className="px-4 py-3">
                        <p className="text-white text-sm font-medium">{row.name}</p>
                        <p className="text-white/45 text-xs mt-0.5">{row.phone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white/80 text-sm">{COUNTRY_LABELS[row.country] ?? row.country}</p>
                        <p className="text-white/40 text-xs mt-0.5 max-w-[140px] truncate">{row.visaCategory}</p>
                      </td>
                      <td className="px-4 py-3 text-white/60 text-sm">{row.travelTimeline}</td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{ background: sc.bg, color: sc.color }}
                        >
                          {sc.icon}
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 min-w-[100px]">
                        <ProbabilityBar value={row.probability} />
                      </td>
                      <td className="px-4 py-3 text-white/50 text-sm">{row.assignedTo ?? "—"}</td>
                      <td className="px-4 py-3 text-white/40 text-xs">{formatDate(row.createdAt)}</td>
                      <td className="px-4 py-3">
                        <ChevronRight className="w-4 h-4 text-white/25" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Right: Detail Panel ── */}
      {selectedId && (
        <div className="w-96 flex-shrink-0" style={{ background: "rgba(255,255,255,0.02)" }}>
          <DetailPanel
            id={selectedId}
            onClose={() => setSelectedId(null)}
            onUpdated={() => refetch()}
          />
        </div>
      )}
    </div>
  );
}
